import { Link } from "react-router-dom";
import { AlertTriangle, FileText, Paperclip } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatFileSize } from "@/api/clientMaterials";
import { formatKbDate, kbFileExt } from "@/api/knowledge";

/**
 * D-147 W1 S3 — спільні реюзабельні шматки reader-екранів «Бази знань»
 * (visual package v1.2 LOCK): рядок матеріалу · sec-title · error-банер
 * keep-stale · skeleton-рядки. Guardrails A11: справжні <Link>/<button>,
 * DS-іконки (lucide, НЕ emoji), DS-токени.
 */

/** Іконка типу: сторінка = info, файл = violet (мокап ric-page/ric-file). */
export function KbTypeIcon({ type }: { type: "page" | "file" }) {
  return type === "file" ? (
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet text-violet-foreground">
      <Paperclip className="h-4 w-4" aria-hidden />
    </span>
  ) : (
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-info text-info-foreground">
      <FileText className="h-4 w-4" aria-hidden />
    </span>
  );
}

/** «файл PDF · 0,4 МБ» / «сторінка» — суфікс типу в rsub-рядку. */
export function kbTypeSuffix(
  type: "page" | "file",
  originalFilename?: string,
  fileSize?: number | null,
): string {
  if (type === "page") return "сторінка";
  const ext = kbFileExt(originalFilename);
  const size = fileSize != null ? ` · ${formatFileSize(fileSize)}` : "";
  return `файл${ext ? ` ${ext}` : ""}${size}`;
}

/** Warning-чіп draft-стану («чернетка» / «є неопубліковані зміни»). */
export function KbDraftChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-warning px-2 py-px text-[11px] font-semibold text-warning-foreground">
      {children}
    </span>
  );
}

export function KbRow({
  to,
  type,
  title,
  sub,
  right,
  chip,
}: {
  to: string;
  type: "page" | "file";
  title: string;
  sub: string;
  right?: string;
  /** editor+ draft-чіп у sub-рядку (S4). */
  chip?: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex min-h-[64px] items-center gap-3 border-b px-4 py-2.5 last:border-b-0 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
    >
      <KbTypeIcon type={type} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{title}</span>
        {(sub || chip) && (
          <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {sub && <span className="truncate">{sub}</span>}
            {chip}
          </span>
        )}
      </span>
      {right && <span className="hidden flex-none text-xs text-muted-foreground md:block">{right}</span>}
    </Link>
  );
}

export function kbPublishedRight(lastPublishedAt: string): string {
  const d = formatKbDate(lastPublishedAt);
  return d ? `опубліковано ${d}` : "";
}

export function KbSecTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2.5 mt-6 flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-muted-foreground first:mt-0">
      {children}
    </h2>
  );
}

/**
 * Error-банер keep-stale-on-error: показується НАД застарілим вмістом, який
 * лишається на екрані; «Повторити» = refetch (visual package: Loading/Error).
 */
export function KbErrorBanner({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-warning px-3.5 py-2.5 text-sm text-warning-foreground"
    >
      <AlertTriangle className="h-4 w-4 flex-none" aria-hidden />
      <span className="min-w-0 flex-1">{text}</span>
      <button type="button" onClick={onRetry} className="font-semibold underline">
        Повторити
      </button>
    </div>
  );
}

/** Повноекранний error (жодних stale-даних): картка + «Повторити». */
export function KbErrorCard({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border bg-card px-4 py-12 text-center">
      <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" aria-hidden />
      <div className="text-sm font-medium">{text}</div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
      >
        Повторити
      </button>
    </div>
  );
}

/** Generic-404 (P12: недоступне == неіснуюче, нуль підтвердження існування). */
export function KbNotFound({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
      <div className="text-sm font-medium">{text}</div>
      <Link
        to="/knowledge"
        className="mt-3 inline-block rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
      >
        До Бази знань
      </Link>
    </div>
  );
}

export function KbRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rounded-xl border">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function KbTilesSkeleton({ tiles = 3 }: { tiles?: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: tiles }, (_, i) => (
        <div key={i} className="rounded-xl border p-4">
          <Skeleton className="mb-2 h-4 w-3/4" />
          <Skeleton className="mb-4 h-3 w-full" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}
